import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {UserCardResponse} from '../interface/userCardResponse';
import {UserCardService} from '../service/user-card.service';
import {TelcellService} from '../service/telcell.service';
import {IdramService} from '../service/idram.service';
import {InitPaymentRequest} from '../models/payment/initPaymentRequest';
import {InitPaymentResponse} from '../models/payment/initPaymentResponse';
import {MobileDetectionService} from '../service/mobile-detection.service';
import {CreditCardService} from '../service/credit-card.service';
import {CreditCardResponse} from '../models/creditCardResponse';
import {WalletToWalletPaymentReceipt} from '../models/payment/walletToWalletPaymentReceipt';
import {MockupDataModel} from '../qr-copy-message/mockup-data.model';
import {SnackBarUtil} from '../qr-copy-message/snack-bar-util.service';
import {PaymentMethod} from '../cards-by-qr/cards-by-qr-payment/payment-method.enum';
import {AmeriaWalletToWalletService} from '../service/ameria-wallet-to-wallet.service';
import {AMERIA_FORM_APPLE_PAY, AMERIA_FORM_CREDIT_CARD, PAYMENT_ID_NAME} from '../constants/payment.constant';
import {DUPLICATE_MONEY_LUX_UUID_MESSAGE} from '../constants/global.constants';
import {EasywalletService} from '../service/easywallet.service';
import {PaymentQrCodeService} from '../service/payment-qr-code-service';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {QrGenerateComponent} from '../qr-generate/qr-generate.component';
import {PaymentQrCodeRequest} from '../interface/paymentQrCodeRequest';
import {LinkUtils} from '../utilityClass/linkUtils';
import {MyPayService} from '../service/my-pay.service';
import {FastshiftService} from '../service/fastshift.service';
import {COPIED_MASSAGE} from '../subscription-payment/subscription-payment.constant';
import {TELCELL_PLACEHOLDER} from '../constants/telcell-placeholder.constants';
import {TranslateService} from '@ngx-translate/core';
import {PaymentMethodConfigService} from '../service/payment-method-config.service';
import {PaymentMethodConfig} from '../models/payment-method-config.model';
import {MenuItemView} from '../interface/menuItemView';
import {MenuService} from '../service/menu.service';
import {CurrencyService} from '../service/currency.service';
import {SharedConstants} from '../utilityClass/sharedConstants';
import {CurrencyResponse} from '../interface/currencyResponse';

interface SelectedMenuItem extends MenuItemView {
  selected?: boolean;
  selectedQuantity?: number;
}

type PaymentCategoryAction = 'pay' | 'redirect';

interface PaymentCategory {
  code: string;
  alt: string;
  action: PaymentCategoryAction;
  paymentMethod?: PaymentMethod;
  disabled?: boolean;
  imageClass?: string;
}

@Component({
  selector: 'app-menu-payment',
  templateUrl: './menu-payment.component.html',
  styleUrls: ['./menu-payment.component.scss']
})
export class MenuPaymentComponent implements OnInit, OnDestroy {

  protected readonly PAY_PRODUCT: string = 'Menu payment for card: ';
  protected readonly PAY_VALID_DAYS: number = 1;
  protected readonly PaymentMethod = PaymentMethod;
  readonly paymentCategories: PaymentCategory[] = [
    {code: 'APPLE_PAY', alt: 'Apple Pay', action: 'pay', paymentMethod: PaymentMethod.APPLE_PAY},
    {code: 'GOOGLE_PAY', alt: 'Google Pay', action: 'pay', paymentMethod: PaymentMethod.NEW_CREDIT_CARD, disabled: true},
    {code: 'IDRAM', alt: 'Idram', action: 'pay', paymentMethod: PaymentMethod.IDRAM},
    {code: 'TELCELL', alt: 'Telcell', action: 'redirect'},
    {code: 'EASY_WALLET', alt: 'Easy Wallet', action: 'pay', paymentMethod: PaymentMethod.EASY_WALLET},
    {code: 'FASTSHIFT', alt: 'Fastshift', action: 'pay', paymentMethod: PaymentMethod.FASTSHIFT},
    {code: 'INECO_PAY', alt: 'Ineco Pay', action: 'pay', paymentMethod: PaymentMethod.NEW_CREDIT_CARD, disabled: true},
    {code: 'MY_PAY', alt: 'My Pay', action: 'pay', paymentMethod: PaymentMethod.MY_PAY, imageClass: 'my-pay-button'},
    {code: 'BANK_OF_GEORGIA', alt: 'Bank of Georgia', action: 'pay', paymentMethod: PaymentMethod.NEW_CREDIT_CARD, disabled: true, imageClass: 'image-height'},
    {code: 'IO_MONEY', alt: 'IO Money', action: 'pay', paymentMethod: PaymentMethod.NEW_CREDIT_CARD, disabled: true},
    {code: 'SBER_BANK', alt: 'Sber Bank', action: 'pay', paymentMethod: PaymentMethod.NEW_CREDIT_CARD, disabled: true},
    {code: 'T_BANK', alt: 'T Bank', action: 'pay', paymentMethod: PaymentMethod.NEW_CREDIT_CARD, disabled: true}
  ];

  paymentMethodConfigs: PaymentMethodConfig[] = [];
  userCard?: UserCardResponse;
  menuId: string = '';
  menuName: string = '';
  selectedItems: SelectedMenuItem[] = [];
  total: number = 0;
  qrCode: string = '';
  isMobile: boolean = false;
  isPayClicked: boolean = false;
  currencyRate: number = SharedConstants.DOLLAR_EXCHANGE_RATE as unknown as number;

  protected creditCard?: CreditCardResponse;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userCardService: UserCardService,
    private telcellService: TelcellService,
    private idram: IdramService,
    private creditCardService: CreditCardService,
    private snackBar: SnackBarUtil,
    private ameriaWalletToWalletService: AmeriaWalletToWalletService,
    private easywalletService: EasywalletService,
    private paymentQrCodeService: PaymentQrCodeService,
    private dialog: MatDialog,
    private linkUtils: LinkUtils,
    private myPayService: MyPayService,
    private fastshiftService: FastshiftService,
    private translateService: TranslateService,
    private paymentMethodConfigService: PaymentMethodConfigService,
    private menuService: MenuService,
    private currencyService: CurrencyService
  ) {
  }

  ngOnInit(): void {
    const state = window.history.state;
    this.selectedItems = state?.items || [];
    this.total = state?.total || 0;
    this.menuName = state?.menuName || '';
    this.qrCode = state?.qrCode || '';

    this.menuId = this.route.snapshot.params['id'];
    this.isMobile = MobileDetectionService.isMobile();

    this.paymentMethodConfigService.getAll().subscribe({
      next: (configs) => this.paymentMethodConfigs = configs
    });

    this.loadCurrencyRate();

    this.creditCardService.getActive().subscribe({
      next: (card: CreditCardResponse) => {
        this.creditCard = card;
      }
    });

    this.loadUserCard();
  }

  private loadUserCard(): void {
    if (this.qrCode) {
      this.userCardService.getCardByQRCode(this.qrCode).subscribe({
        next: (response: UserCardResponse) => {
          this.userCard = response;
        },
        error: () => this.router.navigate(['/error-page'])
      });
      return;
    }

    this.menuService.getMenuById(Number(this.menuId)).subscribe({
      next: (menu) => {
        const linkedQrCode = menu.linked_users?.[0];
        if (!linkedQrCode) {
          this.router.navigate(['/error-page']);
          return;
        }
        this.userCardService.getCardByQRCode(linkedQrCode).subscribe({
          next: (response: UserCardResponse) => {
            this.userCard = response;
          },
          error: () => this.router.navigate(['/error-page'])
        });
      },
      error: () => this.router.navigate(['/error-page'])
    });
  }

  ngOnDestroy(): void {
  }

  get inputValue(): string {
    return String(this.total);
  }

  get totalUsd(): number {
    return this.toUsd(this.total);
  }

  toUsd(amd: number | string | undefined | null): number {
    if (amd === null || amd === undefined || amd === '') return 0;
    const value = typeof amd === 'number' ? amd : Number(String(amd).replace(/[^\d.]/g, ''));
    if (!Number.isFinite(value) || !this.currencyRate) return 0;
    return value / this.currencyRate;
  }

  private loadCurrencyRate(): void {
    this.currencyService.getCurrency(SharedConstants.USD).subscribe({
      next: (data: CurrencyResponse) => {
        const rate = Number(data?.[SharedConstants.USD]);
        if (Number.isFinite(rate) && rate > 0) {
          this.currencyRate = rate;
        }
      }
    });
  }

  get productDescription(): string {
    return this.PAY_PRODUCT + (this.userCard?.cardId ?? '');
  }

  get purchasedProducts(): string {
    return this.selectedItems
      .map(item => {
        const quantity = item.hasQuantity ? (item.selectedQuantity || 1) : 1;
        return `${item.name} (${quantity})`;
      })
      .join(', ');
  }

  get purchasedMenuItemsPayload(): { itemId: number; quantity?: number | null }[] {
    return this.selectedItems.reduce<{ itemId: number; quantity?: number | null }[]>((acc, item) => {
      const parsedItemId = Number(item.id);
      if (Number.isNaN(parsedItemId)) {
        return acc;
      }

      acc.push({
        itemId: parsedItemId,
        quantity: item.hasQuantity ? (item.selectedQuantity || 1) : 1
      });
      return acc;
    }, []);
  }

  get getCreditCardLastNumbers(): string | undefined {
    return this.creditCard?.cardNumber;
  }

  goBack(): void {
    this.router.navigate([`/menu/view/${this.menuId}`]);
  }

  pay(paymentMethod: PaymentMethod): void {
    if (!this.total || this.isPayClicked || !this.userCard) return;
    this.isPayClicked = true;
    switch (paymentMethod) {
      case PaymentMethod.TELCELL:
        this.payWithTelcell();
        break;
      case PaymentMethod.NEW_CREDIT_CARD:
        this.payWithCard();
        break;
      case PaymentMethod.APPLE_PAY:
        this.payWithCard(true);
        break;
      case PaymentMethod.IDRAM:
        this.payWithIdram();
        break;
      case PaymentMethod.EASY_WALLET:
        this.payWithEasyWallet();
        break;
      case PaymentMethod.MY_PAY:
        this.payWithMyPay();
        break;
      case PaymentMethod.FASTSHIFT:
        this.payWithFastshift();
        break;
      case PaymentMethod.EXISTING_CREDIT_CARD:
        this.payWithExistingCreditCard();
        break;
    }
  }

  payWithIdram(): void {
    this.idram.initPayment(this.userCard!, this.inputValue, this.productDescription, true, null);
    this.isPayClicked = false;
  }

  payWithCard(isApplePay: boolean = false): void {
    const userId = this.userCard?.userId!;
    const request = InitPaymentRequest.initForWalletToWallet(
      this.productDescription,
      Number(this.inputValue),
      userId,
      this.userCard!.cardId,
      '',
      null,
      Number(this.menuId),
      this.purchasedProducts,
      this.purchasedMenuItemsPayload
    );
    this.ameriaWalletToWalletService.init(request, true).subscribe({
      next: (paymentResponse: InitPaymentResponse) => {
        const formUrl = isApplePay ? AMERIA_FORM_APPLE_PAY : AMERIA_FORM_CREDIT_CARD;
        window.location.href = formUrl.replace(PAYMENT_ID_NAME, paymentResponse.PaymentID);
      },
      error: (err: any) => {
        if (err.status === 511) {
          this.showSnackbar(MockupDataModel.error(DUPLICATE_MONEY_LUX_UUID_MESSAGE), SnackBarUtil.CLASSES.error);
        } else {
          const msg = this.translateService.instant('payment_execution_error');
          this.showSnackbar(MockupDataModel.error(msg), SnackBarUtil.CLASSES.error);
        }
        this.isPayClicked = false;
      },
      complete: () => {
        this.isPayClicked = false;
      }
    });
  }

  payWithExistingCreditCard(): void {
    const userId = this.userCard?.userId!;
    const request = InitPaymentRequest.initForWalletToWallet(
      this.productDescription,
      Number(this.inputValue),
      userId,
      this.userCard!.cardId,
      this.creditCard!.cardHolderId,
      null,
      Number(this.menuId),
      this.purchasedProducts,
      this.purchasedMenuItemsPayload
    );
    this.ameriaWalletToWalletService.makeBindingPayment(request, true).subscribe({
      next: (paymentResponse: WalletToWalletPaymentReceipt) => {
        this.router.navigate(['payment/receipt'], {state: {receipt: paymentResponse}});
      },
      error: () => {
        const msg = this.translateService.instant('payment_execution_error');
        this.showSnackbar(MockupDataModel.error(msg), SnackBarUtil.CLASSES.error);
        this.isPayClicked = false;
      }
    });
  }

  payWithTelcell(): void {
    if (!this.isMobile) {
      this.telcellService.initPayment(this.inputValue, this.productDescription, String(this.PAY_VALID_DAYS), this.userCard!, true, null);
      this.isPayClicked = false;
    } else {
      this.telcellService.createNewAccount(this.inputValue, this.userCard?.cardId!, true, null).subscribe({
        next: (invoiceId: string) => {
          window.location.href = `https://telcellwallet.page.link/?link=https://com.telcell.app?actionType%3DinvoiceId%26actionValue%3D${invoiceId}&apn=ru.deltapay.puntopago.telcell&isi=1324511564&ibi=com.telcell.app`;
          this.isPayClicked = false;
        },
        error: () => {
          this.telcellService.initPayment(this.inputValue, this.productDescription, String(this.PAY_VALID_DAYS), this.userCard!, true, null);
          this.isPayClicked = false;
        }
      });
    }
  }

  payWithEasyWallet(): void {
    this.easywalletService.initPayment(this.inputValue, this.productDescription, String(this.PAY_VALID_DAYS), this.userCard!, true, null);
    this.isPayClicked = false;
  }

  payWithMyPay(): void {
    this.myPayService.initPayment(this.inputValue, this.userCard!, true);
    this.isPayClicked = false;
  }

  payWithFastshift(): void {
    this.fastshiftService.initPayment(this.inputValue, this.productDescription, String(this.PAY_VALID_DAYS), this.userCard!, true, null);
    this.isPayClicked = false;
  }

  redirectTelcellApp(value: string): void {
    navigator.clipboard.writeText(value).then(() => {
      this.showSnackbar(
        MockupDataModel.success(this.translateService.instant(COPIED_MASSAGE)),
        SnackBarUtil.CLASSES.success
      );
      window.open('https://qrtelcell.onelink.me/1hAx/sg625', '_blank');
    });
  }

  onPaymentCategoryClick(category: PaymentCategory): void {
    if (category.disabled) return;
    if (category.action === 'redirect') {
      this.redirectTelcellApp(TELCELL_PLACEHOLDER);
      return;
    }
    if (category.paymentMethod !== undefined) {
      this.pay(category.paymentMethod);
    }
  }

  showQR(event: any): void {
    if (!this.userCard) return;
    event.stopPropagation();
    const paymentQrCodeRequest: PaymentQrCodeRequest = {
      cardId: this.userCard.id,
      amount: this.total,
      isCommissionActive: true
    };
    this.paymentQrCodeService.generateQRCode(paymentQrCodeRequest).subscribe({
      next: (qrCodeBlob: Blob) => {
        const qrUrl = URL.createObjectURL(qrCodeBlob);
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = {
          userCard: this.userCard,
          qrCodeUrl: qrUrl,
          amount: this.inputValue,
          isUserCardPayment: true,
          isCommissionActive: true,
        };
        dialogConfig.panelClass = 'qr-dialog';
        this.dialog.open(QrGenerateComponent, dialogConfig);
      }
    });
  }

  shareLink(): void {
    const shareUrl = this.linkUtils.getPaymentCardLink(this.inputValue, true);
    navigator.clipboard.writeText(shareUrl).then(() => {
      const message = this.translateService.instant('pay_url_copy_success_alert');
      if (!this.isMobile) {
        this.showSnackbar(MockupDataModel.success(message), SnackBarUtil.CLASSES.success);
      }
      if (navigator.share) {
        navigator.share({title: this.translateService.instant('fee.payment_link'), url: shareUrl});
      }
    });
  }

  getLogoUrl(code: string): string {
    const config = this.paymentMethodConfigs.find(m => m.code === code);
    return config?.logoUrl ?? '';
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBar.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data,
    });
  }
}
